import type { TranslationDictionary } from '../types';

export const en: TranslationDictionary = {
  common: {
    appName: 'ApnaBlock',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    back: 'Back',
    search: 'Search...',
    filter: 'Filter',
    refresh: 'Refresh',
    submit: 'Submit',
    loading: 'Loading...',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
    active: 'Active',
    inactive: 'Inactive',
    open: 'Open',
    pending: 'Pending',
    approved: 'Approved',
    rejected: 'Rejected',
    completed: 'Completed',
    inProgress: 'In Progress',
    resolved: 'Resolved',
    viewDetails: 'View Details',
    download: 'Download',
    print: 'Print',
    export: 'Export',
    actions: 'Actions',
    status: 'Status',
    date: 'Date',
    total: 'Total',
    all: 'All',
    reset: 'Reset',
    more: 'More',
    select: 'Select',
    language: 'Language',
    selectLanguage: 'Select Language',
    saveChanges: 'Save Changes',
    savedSuccessfully: 'Saved successfully',
    confirm: 'Confirm',
    home: 'Home',
    favorites: 'Favorites',
    pinned: 'Pinned',
    tenant: 'Tenant',
    openMenu: 'Open menu',
    expandMenu: 'Expand menu',
    minimizeMenu: 'Minimize menu',
    lightMode: 'Light mode',
    darkMode: 'Dark mode',
    accountMenu: 'Account menu',
    commandPalette: 'Command Palette',
    searchPages: 'Search pages...',
    favorite: 'Favorite',
    pin: 'Pin',
    calendar: 'Calendar',
    inbox: 'Inbox',
    ledger: 'Ledger',
    viewAll: 'View all',
    platformNoSociety: 'Platform (No Society)',
    noSociety: 'No Society',
    trial: 'Trial',
    expired: 'Expired',
    suspended: 'Suspended',
    emptyTitle: 'Nothing here yet',
    emptyDescription: 'Records and updates will appear here once available.',
    tryAgain: 'Try Again',
    somethingWentWrong: 'Something went wrong',
    goBack: 'Go Back',
    goToHome: 'Go to Home',
    referenceId: 'Reference ID',
    or: 'OR',
  },
  auth: {
    login: 'Sign In',
    logout: 'Log Out',
    profile: 'Profile',
    email: 'Email Address',
    password: 'Password',
    forgotPassword: 'Forgot Password?',
    sendOtp: 'Send OTP',
    verifyOtp: 'Verify OTP',
    mobile: 'Mobile Number',
    rememberMe: 'Remember Me',
    biometricLogin: 'Biometric Login',
    changePassword: 'Change Password',
    signOut: 'Sign Out',
    welcome: 'Welcome',
    signIn: 'Welcome back',
    signInSubtitle: 'Sign in to your society workspace securely.',
    signingInTo: 'Signing in to {{name}}',
    method: 'Method',
    emailTab: 'Email',
    mobileTab: 'Mobile',
    otpTab: 'OTP',
    emailLabel: 'Email',
    mobileLabel: 'Mobile',
    passwordLabel: 'Password',
    enableBiometricAfterLogin: 'Enable biometric login after sign-in',
    signInWithBiometrics: 'Sign in with biometrics',
    biometricDesktopInfo: 'Biometric login is available in the ApnaBlock mobile app.',
    verifyAndSignIn: 'Verify & sign in',
    changeIdentifier: 'Change identifier',
    enterOtpSentTo: 'Enter the OTP sent to your {{channel}}.',
    otpCodeLabel: 'OTP code',
    channelLabel: 'Channel',
    emailChannel: 'Email',
    smsChannel: 'SMS',
    forgotPasswordTitle: 'Forgot password',
    forgotPasswordSubtitle: 'Enter your email or mobile. We will send an OTP to reset your password.',
    emailOrMobile: 'Email or mobile',
    backToSignIn: 'Back to sign in',
    resetPasswordTitle: 'Reset password',
    resetPasswordSubtitle: 'Enter the OTP sent to your email or mobile, then choose a new password.',
    newPasswordLabel: 'New password',
    confirmPasswordLabel: 'Confirm password',
    updatePassword: 'Update password',
    changePasswordTitle: 'Change password',
    changePasswordSubtitle: 'Update your account password. You will stay signed in.',
    currentPasswordLabel: 'Current password',
    confirmNewPasswordLabel: 'Confirm new password',
    savePassword: 'Save password',
    passwordRequirementNotice: 'Use at least 8 characters with uppercase, lowercase, a number, and a special character.',
    bannerTagline: 'Enterprise society operations — banking, collections, meetings, and residents in one place.',
    bannerSubtext: 'Secure multi-tenant platform for colonies and housing societies.',
    biometricMobileOnly: 'Biometric login is available only on the mobile app.',
    signedInBiometrics: 'Signed in with biometrics',
    biometricSessionExpired: 'Session expired or revoked. Please sign in with your password.',
    biometricAuthFailed: 'Biometric authentication failed. Please use password.',
    biometricEnrollSuccess: 'Biometric login enabled',
    biometricEnrollCancelled: 'Biometric enrollment cancelled or failed. Normal login succeeded.',
    welcomeMarketing: 'Welcome to Marketing Partner Portal',
    welcomePlatform: 'Welcome to Platform Administration',
    welcomeBackToast: 'Welcome back',
    otpSentSuccess: 'OTP sent successfully',
    otpVerifyFailed: 'OTP verification failed',
    loginFailed: 'Email or password is incorrect.',
    biometricAuthReason: 'Verify your identity to sign in to ApnaBlock',
    biometricAvailableFor: 'Biometric login available for {{identifier}}',
    biometricEnrollPrompt: 'Enable biometric login for {{name}}?',
  },
  nav: {
    dashboard: 'Dashboard',
    society: 'Society',
    societySettings: 'My Society',
    societyBlocks: 'Blocks / Towers',
    societyHouses: 'Houses',
    societyResidents: 'Residents',
    societyStaff: 'Staff',
    imports: 'Import Data',
    finance: 'Finance',
    financePayments: 'My payments',
    financeCollections: 'Collections',
    financeSocietyFund: 'Society Fund',
    financeSpecialCollections: 'Special collections',
    financeCorpusFund: 'Corpus Fund',
    financeExpenses: 'Expenses',
    financeExpenseGroups: 'Expense Groups',
    financeWaterTanker: 'Water Tanker',
    financeReimbursements: 'Claim reimbursement',
    financeBanking: 'Bank Accounts',
    financeStatements: 'Statements & Receipts',
    financeReports: 'Financial Reports',
    financeReconciliation: 'Payment Reconciliation',
    security: 'Security & Gate',
    gatePasses: 'Gate Passes',
    securityDashboard: 'Security Dashboard',
    securityGates: 'Gate Management',
    securityShifts: 'Watchman Shift Reports',
    securityPolicies: 'Security Policies',
    securityDeskPortal: 'Security Gate Desk',
    visitors: 'Visitors Desk',
    securityCameras: 'CC Cameras',
    community: 'Community',
    notices: 'Notices',
    communityForum: 'Community Forum',
    events: 'Events',
    eventsList: 'Events',
    eventsCalendar: 'Event Calendar',
    invitations: 'Invitations & RSVPs',
    whatsapp: 'WhatsApp Logs',
    notifications: 'Notifications',
    operations: 'Operations',
    complaints: 'Complaints',
    serviceRequests: 'Service Requests',
    serviceDirectory: 'Service Directory',
    amenities: 'Amenities & Facilities',
    meetings: 'Meetings',
    documents: 'Document Vault',
    governance: 'Governance',
    polls: 'Resident Polls',
    elections: 'Elections',
    governancePetitions: 'File petition',
    governanceResignation: 'Submit resignation',
    administration: 'Administration',
    adminLicense: 'License',
    adminInvoices: 'Subscription invoices',
    adminRoles: 'Roles & Permissions',
    adminLetterhead: 'Letterhead & Branding',
    adminIntegrations: 'SMS & WhatsApp',
    adminAiConfig: 'AI Configuration',
    adminNavigation: 'Menu Customization',
    adminAudit: 'Audit Logs',
    documentationGroup: 'Help & Documentation',
    societySupportDesk: 'Help & Support',
    docsKnowledgeBase: 'Knowledge Base',
    docsRoleMatrix: 'Role & Permission Matrix',
    docsQaHub: 'QA Test Hub',
    docsTestExplorer: 'Test Case Explorer',
    licenseLocked: 'License',
    platformSocietiesGroup: 'Societies & Tenants',
    platformSocieties: 'All Societies',
    platformSocietiesNew: 'Register Society',
    platformSocietyTypes: 'Society Types & Taxonomy',
    platformTrials: 'Free Trials & Onboarding',
    platformSubscriptions: 'Subscriptions',
    platformLicenses: 'Software Licenses',
    platformPlans: 'Subscription Plans',
    platformInvoices: 'Billing & Invoices',
    platformPricingOverrides: 'Pricing Overrides',
    platformCoupons: 'Coupons & Discounts',
    platformPromotionGroup: 'Growth & Promotion',
    platformPromotionDashboard: 'Growth Dashboard',
    platformPromotionProspects: 'Prospects & Leads',
    platformPromotionCampaigns: 'Campaigns',
    platformPromotionTemplates: 'Message Templates',
    platformPromotionSenderProfiles: 'Sender Profiles',
    platformPromotionEmail: 'Email Marketing',
    platformPromotionWhatsapp: 'WhatsApp Broadcasts',
    platformPromotionSms: 'SMS Marketing',
    platformPromotionOther: 'Other Channels',
    platformReferrals: 'Affiliates & Referrals',
    platformMarketing: 'Marketing Personnel',
    platformPromotionAnalytics: 'Promotion Analytics',
    platformPromotionSettings: 'Marketing Settings',
    platformManagementGroup: 'Platform & Support',
    platformWebsite: 'Website CMS & Branding',
    platformSupportTickets: 'Support Tickets Desk',
    platformFeatureRequests: 'Feature Requests',
    platformFeatures: 'Feature Flags',
    platformUsers: 'Platform Administrators',
    platformCoverageAudit: 'Docs Coverage Audit',
    platformQaGroup: 'QA & Testing',
    platformQaCenter: 'Overview',
    platformQaOverview: 'Overview',
    platformQaSuites: 'Test Suites',
    platformQaRuns: 'Test Runs',
    platformQaCoverage: 'Test Coverage',
    platformQaGates: 'Release Readiness',
    platformReportsGroup: 'Analytics & Reports',
    platformAnalytics: 'Platform Analytics',
    platformSystemGroup: 'System & Security',
    platformSystemHealth: 'System Health',
    platformAuditLogs: 'Audit Logs',
    platformNotifications: 'Alerts & Notifications',
    platformSettings: 'Global Settings',
    platformDataCleanup: 'Data Cleanup',
    platformGoogleCalendar: 'Google Calendar Integration',
    platformMaintenance: 'Database Maintenance',
    directory: 'Resident Directory',
    members: 'Members',
    houses: 'Units & Flats',
    settings: 'Settings',
    myUnit: 'My Unit',
    waterTankers: 'Water Tankers',
    patrol: 'Guard Patrols',
    cameras: 'Live CCTV',
    marketplace: 'Marketplace',
    profile: 'My Profile',
    superAdmin: 'Platform Admin',
    staff: 'Society Staff',
    helpDesk: 'Helpdesk',
    favorites: 'Favorites',
    pinned: 'Pinned',
    navGroupHome: 'HOME',
    navGroupSociety: 'MY SOCIETY',
    navGroupMoney: 'MONEY',
    navGroupSecurity: 'SECURITY',
    navGroupCommunity: 'COMMUNITY',
    navGroupManagement: 'MANAGEMENT',
    navGroupAdmin: 'ADMIN',
    navGroupHelp: 'HELP & SUPPORT',
    securityNotificationSettings: 'Security Policies',
    securityCcCameras: 'CC Cameras',
  },
  mobile: {
    greetingMorning: 'Good morning, {{name}}',
    greetingAfternoon: 'Good afternoon, {{name}}',
    greetingEvening: 'Good evening, {{name}}',
    greetingNight: 'Good night, {{name}}',
    greetingGeneric: 'Welcome, {{name}}',
    priorityAttention: 'Needs Attention',
    priorityAllClear: 'All dues cleared',
    priorityAllClearSub: 'No pending maintenance or bills',
    payMaintenance: 'Pay Maintenance',
    payDues: 'Pay Now',
    viewDues: 'View Dues',
    viewReceipt: 'View Receipt',
    dueBy: 'Due by {{date}}',
    quickActions: 'Quick Actions',
    actionPay: 'Pay Dues',
    actionVisitors: 'Visitors',
    actionRaiseIssue: 'Raise Issue',
    actionNotices: 'Notice Board',
    actionBookFacility: 'Book Facility',
    actionEmergency: 'Emergency SOS',
    actionDirectory: 'Directory',
    actionPolls: 'Vote & Polls',
    actionMeetings: 'Meetings',
    societyUpdates: 'Society Updates',
    gateAndVisitors: 'Gate & Visitors',
    upcomingEvents: 'Upcoming Events',
    maintenanceAndReceipts: 'Maintenance & Receipts',
    pendingApprovals: 'Pending Approvals',
    collectionsOverview: 'Collections Overview',
    pendingReview: 'Pending Review',
    noPendingReviews: 'All reviews up to date',
    totalCollected: 'Total Collected',
    totalOutstanding: 'Total Outstanding',
    activeGatePasses: 'Active Gate Passes',
    noVisitorsToday: 'No visitors logged today',
    noActivePasses: 'No active visitor passes',
    preApproveGuest: 'Pre-approve Guest',
    recentReceipts: 'Recent Receipts',
    noRecentReceipts: 'No recent transactions',
    managementDesk: 'Management Desk',
    switchRole: 'Switch View',
    residentView: 'Resident View',
    committeeView: 'Committee View',
    emergencyModalTitle: 'Emergency SOS Alert',
    emergencyModalSubtitle: 'Broadcast an urgent distress signal to the security guard and society committee',
    tapToCall: 'Call Guard',
    tapToWhatsApp: 'WhatsApp Office',
    pullToRefresh: 'Pull down to refresh',
    refreshing: 'Refreshing…',
    noMeetingsScheduled: 'No meetings scheduled',
    noNoticesPublished: 'No announcements published yet',
    noUpcomingEvents: 'No upcoming events',
    viewAllNotices: 'View all notices',
    viewAllEvents: 'View all events',
    viewAllComplaints: 'View all issues',
    sectionDiscover: 'Discover',
    sectionEngage: 'Engage',
    sectionPay: 'Pay',
    cardResidents: 'Residents',
    cardResidentsDesc: 'Find people in your society',
    cardLocalHelp: 'Local Help',
    cardLocalHelpDesc: 'Find daily household help',
    cardEmergencyContacts: 'Emergency Contacts',
    cardEmergencyContactsDesc: 'Police, ambulance and important numbers',
    cardHelpDesk: 'Help & Complaints',
    cardHelpDeskDesc: 'Raise an issue or request help',
    cardFacilities: 'Facilities',
    cardFacilitiesDesc: 'Book and use society facilities',
    cardNotices: 'Society Notices',
    cardNoticesDesc: 'Important updates from your society',
    cardCommunity: 'Community',
    cardCommunityDesc: 'Polls, meetings and events',
    cardDocuments: 'Documents',
    cardDocumentsDesc: 'Society and personal documents',
    cardPaymentsDues: 'Payments & Dues',
    cardPaymentsDuesDesc: 'View dues and make a payment',
    cardUtilities: 'Utilities',
    cardUtilitiesDesc: 'Electricity, water and other utilities',
    actionRequired: 'Action Required',
    pendingActionSubtitle: '{{count}} items pending committee review',
    collectionsStatus: 'Collections & Dues',
    uncollectedSocietyDues: '{{count}} pending payments',
    collectedThisMonth: 'Collected this month',
    viewLedger: 'View Ledger',
    committeeActions: 'Committee Actions',
    postNotice: 'Post Notice',
    postNoticeDesc: 'Publish notice to society',
    addExpense: 'Add Expense',
    addExpenseDesc: 'Record society expense',
    scheduleMeeting: 'Schedule Meeting',
    scheduleMeetingDesc: 'Organize society meeting',
    residentDirectory: 'Resident Directory',
    residentDirectoryDesc: 'Search & call residents',
    complaintsOverview: 'Helpdesk & Issues',
    upcomingMeetingsCount: '{{count}} upcoming meeting(s)',
    viewMeetingAgenda: 'View meeting details and agenda',
    resignOfficeTitle: 'Submit resignation',
    resignOfficeNotice: 'Submit formal resignation to society members',
    activeSocietyElections: 'Active Society Elections',
    votingLiveStatus: 'Voting is live for members',
    castYourVotePrompt: 'Cast your vote for nominated committee candidates',
    recentNotices: 'Recent Notices',
    myComplaints: 'My Complaints',
    noActiveComplaints: 'No active complaints or tickets',
  },
  profile: {
    profileTitle: 'Profile & Preferences',
    profileSubtitle: 'Manage your personal details, security settings, and app language.',
    basicDetails: 'Basic Details',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    mobile: 'Mobile Number',
    saveDetails: 'Save Details',
    userId: 'User ID',
    roles: 'Assigned Roles',
    tenant: 'Society / Community',
    securityBiometrics: 'Security & Biometrics',
    securityBiometricsDesc: 'Use fingerprint or facial recognition to quickly and securely unlock ApnaBlock on this device.',
    biometricLoginEnabled: 'Biometric Login Enabled',
    enableBiometricLogin: 'Enable Biometric Login',
    notificationPreferences: 'Notification Preferences',
    notificationPreferencesDesc: 'Control how you receive society notices and alerts.',
    languagePreferences: 'App Language',
    languagePreferencesDesc: 'Choose your default language. ApnaBlock will always open in this language for your account.',
    currentLanguage: 'Current Language',
    selectDefaultLanguage: 'Select Default Language',
    defaultLanguageNotice: 'Your preferred language is saved and synced across all your sessions.',
    saveLanguage: 'Set as Default Language',
    languageUpdated: 'Language preference saved successfully',
  },
  dashboard: {
    welcomeBack: 'Welcome back',
    welcomeBackUser: 'Welcome back, {{name}}',
    societyWorkspace: 'Your society workspace',
    platformSubtitle: 'ApnaBlock · societies and software licenses',
    registerSociety: 'Register society',
    registerSocietyHint: 'Create a tenant and President login',
    allSocieties: 'All societies',
    allSocietiesHint: 'Listing and status',
    softwareLicenses: 'Software licenses',
    softwareLicensesHint: 'Select a society, then issue or review',
    bankBalance: 'Bank balance',
    outstandingDues: 'Outstanding dues',
    residents: 'Residents',
    openRequests: 'Open requests',
    upcomingMeetings: 'Upcoming meetings',
    platform: 'Platform',
    societies: 'Societies',
    licenses: 'Licenses',
    noBankAccounts: 'No bank accounts yet',
    allDuesCleared: 'All dues are cleared',
    noOpenComplaints: 'No open complaints',
    noMeetingsScheduled: 'No meetings scheduled',
    overdueAndOpen: '{{overdue}} overdue · {{open}} open bills',
    openBillsCount: '{{count}} open bill(s)',
    openBillsCountSingle: '1 open bill',
    accountsCountThisMonth: '{{count}} account(s) · in {{amount}} this month',
    accountsCountThisMonthSingle: '1 account · in {{amount}} this month',
    residentsAndHouses: '{{residents}} residents · {{houses}} houses',
    residentsOnRecord: '{{count}} on record',
    housesCount: '{{count}} houses',
    pendingReviewOverdue: '{{pending}} pending review · {{overdue}} overdue',
    pendingReview: '{{count}} pending review',
    statManage: 'Manage',
    statSubscription: 'Subscription',
    collectionsCashflow: 'Collections & cashflow',
    collected: 'Collected',
    outstanding: 'Outstanding',
    thisMonthCashflow: 'This month · in {{credit}} / out {{debit}}',
    cashflow: 'Cashflow',
    quickActions: 'Quick Actions',
    scheduleMeeting: 'Schedule meeting',
    scheduleMeetingHint: 'Notify owners & calendar',
    postNotice: 'Post notice',
    postNoticeHint: 'Announce to the society',
    raiseComplaint: 'Raise complaint',
    raiseComplaintHint: 'Log a service request',
    filePetition: 'File a petition',
    filePetitionHint: 'Sack or demand resignation',
    viewProfile: 'View profile',
    viewProfileHint: 'Account & permissions',
    publicPetitions: 'Public petitions',
    openPetitions: 'Open petitions · visible to all members',
    openBoard: 'Open board',
    noMeetingsDescription: 'Upcoming committee meetings will appear here.',
    announcements: 'Announcements',
    noAnnouncements: 'No announcements',
    noAnnouncementsDesc: 'Published notices will appear here.',
    recentActivity: 'Recent activity',
    noActivity: 'No activity yet',
    noActivityDesc: 'Notifications and updates will appear here.',
    recentTransactions: 'Recent transactions',
    visitorActivity: 'Visitor activity',
    visitorsComingSoon: 'Visitors coming soon',
    visitorsComingSoonDesc: 'Gate logs will appear here when the visitors module is enabled.',
    openVisitors: 'Open visitors',
    recentNotices: 'Recent Notices',
    pendingComplaints: 'Pending Complaints',
    upcomingEvents: 'Upcoming Events',
    maintenanceDue: 'Maintenance Dues',
    visitorsToday: 'Visitors Today',
    viewAll: 'View All',
    societyOverview: 'Society Overview',
    activeNotices: 'Active Notices',
  },
  complaints: {
    title: 'Helpdesk & Complaints',
    createComplaint: 'Raise Complaint',
    category: 'Category',
    priority: 'Priority',
    status: 'Status',
    description: 'Description',
    raisedBy: 'Raised By',
    assignedTo: 'Assigned To',
    resolve: 'Mark Resolved',
    myComplaints: 'My Complaints',
    allComplaints: 'All Society Complaints',
  },
  visitors: {
    title: 'Visitor Management',
    logVisitor: 'Pre-Approve Visitor',
    visitorName: 'Visitor Name',
    phone: 'Phone Number',
    hostUnit: 'Unit / Flat',
    purpose: 'Purpose of Visit',
    vehicleNumber: 'Vehicle Number',
    checkIn: 'Check In',
    checkOut: 'Check Out',
    expectedVisitors: 'Expected Guests',
    activeVisitors: 'Currently Inside',
  },
  finance: {
    title: 'Finance & Payments',
    dues: 'Outstanding Dues',
    payNow: 'Pay Now',
    transactions: 'Transaction History',
    invoices: 'Maintenance Invoices',
    receipts: 'Payment Receipts',
    paymentHistory: 'Payment History',
    maintenanceFee: 'Monthly Maintenance',
    outstandingBalance: 'Current Outstanding',
    paid: 'Paid',
    unpaid: 'Unpaid',
  },
  notices: {
    title: 'Society Notices',
    createNotice: 'Publish Notice',
    important: 'Important Notice',
    publishedOn: 'Published on',
    readMore: 'Read Full Notice',
    attachments: 'Attached Documents',
    societyAnnouncements: 'Community Announcements',
  },
  amenities: {
    title: 'Society Amenities',
    bookAmenity: 'Book Amenity',
    myBookings: 'My Bookings',
    availableSlots: 'Available Time Slots',
    rules: 'Facility Rules & Guidelines',
  },
  elections: {
    title: 'Society Elections & Polls',
    activeElections: 'Active Elections',
    castVote: 'Cast Your Vote',
    candidates: 'Nominated Candidates',
    results: 'Election Results',
  },
  emergency: {
    emergencyAlert: 'EMERGENCY ALERT',
    triggerAlert: 'Trigger SOS Alert',
    fire: 'Fire Emergency',
    medical: 'Medical Emergency',
    security: 'Security Threat',
    panic: 'Panic Button',
    sos: 'SOS Broadcast',
    activeAlert: 'Active Emergency Alert in Progress',
  },
  validation: {
    required: 'This field is required.',
    invalidEmail: 'Please enter a valid email address.',
    invalidMobile: 'Please enter a valid 10-digit mobile number.',
    passwordTooShort: 'Password must be at least 8 characters long.',
    positiveNumber: 'Please enter a positive value.',
  },
  superAdmin: {
    title: 'Platform Administration',
    dashboard: 'Dashboard',
    platformOverview: 'Platform Overview',
    platformOverviewSubtitle: 'Real-time multi-tenant society metrics, subscription health, MRR, and platform activity',
    totalSocieties: 'Total Societies',
    societiesActiveAndTrial: '{{active}} Active · {{trial}} in Trial',
    monthlyRecurring: 'Monthly Recurring (MRR)',
    annualRecurring: 'ARR: {{arr}}',
    activeTrials: 'Active Trials',
    expiringInDays: '{{count}} Expiring in 30 Days',
    totalPlatformUsers: 'Total Platform Users',
    acrossAllManagedSocieties: 'Across all managed societies',
    upcomingExpirations: 'Upcoming Trial & License Expirations (Next 30 Days)',
    upcomingExpirationsSubtitle: 'Societies requiring trial extension, renewal follow-up, or conversion',
    noUpcomingExpirations: 'No upcoming society license expirations in the next 30 days.',
    societyName: 'Society Name',
    plan: 'Plan',
    expiresIn: 'Expires In',
    manage: 'Manage',
    subscriptionTierShare: 'Subscription Tier Share',
    subscriptionTierShareSubtitle: 'Active society distribution across plans',
    noPlanDistribution: 'No plan distribution data available',
    daysCount: '{{count}} days',
    expiredToday: 'Expired today',
    freeTrial: 'Free Trial',
    analytics: 'Platform Analytics',
    societies: 'All Societies',
    societyTypes: 'Society Types',
    trials: 'Free Trials & Onboarding',
    subscriptions: 'Subscriptions',
    licenses: 'Software Licenses',
    plans: 'Subscription Plans',
    invoices: 'Billing & Invoices',
    pricingOverrides: 'Pricing Overrides',
    coupons: 'Coupons & Discounts',
    promotion: 'Growth & Promotion',
    referrals: 'Affiliates & Referrals',
    marketing: 'Marketing Personnel',
    websiteCms: 'Website CMS & Branding',
    features: 'Feature Flags',
    platformUsers: 'Platform Administrators',
    systemHealth: 'System Health',
    qaCenter: 'QA Automation Center',
    auditLogs: 'Audit Logs',
    settings: 'Global Settings',
    dataCleanup: 'Data Cleanup',
    maintenance: 'Database Maintenance',
  },
  tables: {
    noRecords: 'No records found matching criteria',
    rowsPerPage: 'Rows per page:',
    pageOf: 'Page {{page}} of {{totalPages}}',
    filterResults: 'Filter results...',
    showingResults: 'Showing {{count}} results',
  },
  dialogs: {
    confirmAction: 'Confirm Action',
    confirmDelete: 'Confirm Deletion',
    areYouSure: 'Are you sure you want to proceed?',
    irreversibleNotice: 'This action cannot be undone.',
    discardChanges: 'Discard unsaved changes?',
  },
  feedback: {
    savedSuccess: 'Details saved successfully',
    updateSuccess: 'Updated successfully',
    errorOccurred: 'An unexpected error occurred. Please try again.',
    languageChanged: 'Language updated to English',
    biometricEnabledSuccess: 'Biometric authentication enabled',
    biometricDisabledSuccess: 'Biometric authentication disabled',
  },
};
